import React, { useMemo } from 'react';

import { BBoxProps } from './BBox';
import { Character } from './types';
import { useBBoxCornerScaling } from './useBBoxCornerScaling';
import { useGetMousePositionFromEvent } from './useMousePositionFromEvent';

interface BBoxCornerProps {
  cx: React.SVGProps<SVGCircleElement>['cx'];
  cy: React.SVGProps<SVGCircleElement>['cy'];

  characterId: Character['id'];

  cornerPosition: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

  scaleStartCallback: BBoxProps['scaleStartCallback'];
  scalingCallback: BBoxProps['scalingCallback'];
  scaleEndCallback: BBoxProps['scaleEndCallback'];
}

export const BBoxCorner: React.FC<BBoxCornerProps> = ({
  cx,
  cy,
  characterId,
  cornerPosition,
  scaleStartCallback,
  scalingCallback,
  scaleEndCallback,
}) => {
  const getMousePositionFromEvent = useGetMousePositionFromEvent();

  const cursor = useMemo(() => {
    if (cornerPosition === 'top-left' || cornerPosition === 'bottom-right') {
      return 'nwse-resize';
    } else {
      return 'nesw-resize';
    }
  }, [cornerPosition]);

  const {
    scaleFromTopLeft,
    scaleFromTopRight,
    scaleFromBottomLeft,
    scaleFromBottomRight,
  } = useBBoxCornerScaling(characterId);

  const scalingFunction = useMemo(() => {
    switch (cornerPosition) {
      case 'top-left':
        return scaleFromTopLeft;
      case 'top-right':
        return scaleFromTopRight;
      case 'bottom-left':
        return scaleFromBottomLeft;
      case 'bottom-right':
        return scaleFromBottomRight;
    }
  }, [
    cornerPosition,
    scaleFromBottomLeft,
    scaleFromBottomRight,
    scaleFromTopLeft,
    scaleFromTopRight,
  ]);

  return (
    <circle
      style={{ cursor }}
      r={3}
      cx={cx}
      cy={cy}
      fill="white"
      stroke="black"
      onMouseDown={(event) => {
        const {
          x: scalingStartMouseX,
          y: scalingStartMouseY,
        } = getMousePositionFromEvent(event);

        scaleStartCallback();

        const myMoveEventListener = (event: MouseEvent) => {
          scalingFunction(
            event,
            scalingStartMouseX,
            scalingStartMouseY,
            (newX, newY, newWidth, newHeight) => {
              scalingCallback(newX, newY, newWidth, newHeight);
            }
          );
        };

        document.addEventListener('mousemove', myMoveEventListener);

        document.addEventListener(
          'mouseup',
          () => {
            scaleEndCallback();

            document.removeEventListener('mousemove', myMoveEventListener);
          },
          { once: true }
        );

        // to prevent "text selection" issues within the SVG during scaling
        event.preventDefault();
      }}
    />
  );
};
